import {
  useState,
  useEffect,
  useMemo,
  useContext,
  Dispatch,
  SetStateAction,
} from 'react'
import { fetchData } from '../../../global/utils/fetch'
import {
  getSchedulingPerformanceData,
  getSchedulingPerformanceThresholdValues,
} from './api'
import { COLUMN_DEFINITIONS } from './helpers/column-definitions'
import { getFixForLocation } from './helpers/fix-logic'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import { formatDateYyyyMmDd } from '../../../global/utils/date/date-utils-yyyy-mm-dd'
import Dashboard, {
  DashboardColumn,
} from '../../../global/components/dashboard/Dashboard'
import {
  SchedulingPerformanceData,
  SchedulingPerformanceLocationData,
} from '../../../../../app/types/report_data'

interface SchedulingPerformanceProps {
  date: Date
  isTableLoading: boolean
  setIsTableLoading: Dispatch<SetStateAction<boolean>>
}

async function getThresholdValues(setThresholdValues: Function) {
  const thresholdValuesString = await fetchData<string>(
    getSchedulingPerformanceThresholdValues(),
  )
  const thresholdValuesJson: {
    integratedSchedulerRateThreshold: number
    averageScheduleLeadTimeThreshold: number
  } = JSON.parse(thresholdValuesString)
  setThresholdValues(thresholdValuesJson)
}

function SchedulingPerformance({
  date,
  isTableLoading,
  setIsTableLoading,
}: SchedulingPerformanceProps) {
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const [tableState, setTableState] =
    useState<SchedulingPerformanceData | null>(null)
  const [thresholdValues, setThresholdValues] = useState({
    integratedSchedulerRateThreshold: 0.5,
    averageScheduleLeadTimeThreshold: 7,
  })

  useEffect(() => {
    getThresholdValues(setThresholdValues)
  }, [])

  async function fetchDashboardData() {
    setIsTableLoading(true)
    try {
      const data = await fetchData<SchedulingPerformanceData>(
        getSchedulingPerformanceData(date),
      )
      setTableState(data)
    } catch (err) {
      console.error(err)
      setTableState(null)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: `There is no data for ${formatDateYyyyMmDd(date)}`,
        severity: 'error',
      })
    }
    setIsTableLoading(false)
  }

  useEffect(() => {
    fetchDashboardData()
  }, [date])

  const columns: DashboardColumn<SchedulingPerformanceLocationData>[] = useMemo(
    () => [
      {
        Header: 'Location',
        accessor: 'locationName',
      },
      {
        Header: 'Scheduled Orders',
        accessor: 'scheduledOrders',
        tooltip:
          COLUMN_DEFINITIONS.SCHEDULING_PERFORMANCE.total_scheduled_orders,
      },
      {
        Header: 'IS Rate',
        accessor: 'integratedSchedulerRate',
        tooltip:
          COLUMN_DEFINITIONS.SCHEDULING_PERFORMANCE.integrated_scheduler_rate,
      },
      {
        Header: 'Drivetime 0s',
        accessor: 'driveTimeZeroes',
        tooltip: COLUMN_DEFINITIONS.SCHEDULING_PERFORMANCE.drivetime_zeroes,
        Cell: ({ row, value }) => ({
          value,
          tooltip:
            row.driveTimeZeroes > 0 && row.driveTimeZeroZipcodes?.length
              ? `Affected zipcodes:\n${row.driveTimeZeroZipcodes.join('\n')}`
              : undefined,
        }),
      },
      {
        Header: 'Schedule Full 0s',
        accessor: 'scheduleFullZeroes',
        tooltip: COLUMN_DEFINITIONS.SCHEDULING_PERFORMANCE.schedule_full_zeroes,
        Cell: ({ row, value }) => ({
          value,
          tooltip:
            row.scheduleFullZeroes > 0 && row.scheduleFullZeroZipcodes?.length
              ? `Affected zipcodes:\n${row.scheduleFullZeroZipcodes.join('\n')}`
              : undefined,
        }),
      },
      {
        Header: 'Average SLT',
        accessor: 'averageScheduleLeadTime',
        tooltip:
          COLUMN_DEFINITIONS.SCHEDULING_PERFORMANCE.average_schedule_lead_time,
      },
      {
        Header: '7day%Booked',
        accessor: 'sevenDayPercentBooked',
        tooltip:
          COLUMN_DEFINITIONS.SCHEDULING_PERFORMANCE.seven_day_percent_booked,
      },
      {
        Header: 'Fix',
        accessor: (row) => {
          const fixAndTooltip = getFixForLocation(row, thresholdValues)
          return fixAndTooltip?.[0] || ''
        },
        Cell: ({ row }) => {
          const fixAndTooltip = getFixForLocation(row, thresholdValues)
          const isFixRequired = fixAndTooltip && fixAndTooltip[0]
          return {
            value: fixAndTooltip?.[0] || '',
            tooltip: isFixRequired ? fixAndTooltip[1] : undefined,
            color: isFixRequired ? 'var(--status-error-text)' : undefined,
            bold: isFixRequired ? true : false,
          }
        },
      },
    ],
    [thresholdValues],
  )

  const totals = useMemo(() => {
    if (!tableState?.totalData) return undefined
    // totalData has 6 values (scheduled orders through 7day%booked), plus empty for Fix column
    return [...tableState.totalData, '']
  }, [tableState])

  return (
    <Dashboard<SchedulingPerformanceLocationData>
      columns={columns}
      data={tableState?.schedulingPerformanceLocationData ?? []}
      isLoading={isTableLoading}
      totals={totals}
      totalsLabel='TRMI Total'
      getRowKey={(row) => row.locationName}
      noDataMessage='No scheduling performance data available.'
    />
  )
}

export default SchedulingPerformance
